import { Fragment, useState } from 'react'
import { Card, CardBody, Modal } from 'react-bootstrap'
import SelectAutoCompleteFilter from '../../components/SelectAutocompleteFilter'
import { API_URL } from '../../constants'
import { getLabelTipoBilancia } from '../../utils/utils'
import dayjs from 'dayjs'
import 'react-calendar/dist/Calendar.css'
import { useBilanceListInfinite } from '../../hooks/bilance'
import StickyTable from '../../components/StickyTable'
import CalendarField from '../../components/CalendarField'
import { useIsMobile } from '../../hooks/useMediaQuery'
import PianificazioneFooter, {
  PianificazioneStep,
  PianificazioneTabs,
} from './PianificazioneControls'

interface ModalConfirmProps {
  isOpen: boolean
  toggle: () => void
  save: (values: any) => Promise<void>
  cliente_id?: number
}

export default function ModalPianificazioneTaratura({
  isOpen,
  toggle,
  save,
  cliente_id,
}: ModalConfirmProps) {
  const [cliente, setCliente] = useState(cliente_id ? String(cliente_id) : '')

  const {
    data: bilanceInfinite,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useBilanceListInfinite({
    cliente,
    rapporto_taratura: 'true',
    data_intervento_taratura_aperto__isnull: 'true',
  })

  const [bilanceSelected, setBilanceSelected] = useState<number[]>([])
  const [dataIntervento, setDataIntervento] = useState('')
  const [note, setNote] = useState('')
  const [utente, setUtente] = useState<number | null>(null)
  const [step, setStep] = useState<PianificazioneStep>('selezione')
  const isMobile = useIsMobile()

  const operatoreField = (
    <div className={isMobile ? undefined : 'mt-4'}>
      <div className="form-group">
        <label className="label-m text-body-secondary">
          Operatore
        </label>
      </div>
      <SelectAutoCompleteFilter
        value={utente}
        placeholder="Seleziona operatore"
        onChange={(value: any) =>
          setUtente(value ? value.value : null)
        }
        url={`${API_URL}/utenti/users/options-operatori/`}
        isMulti={false}
      />
    </div>
  )

  return (
    <Modal
      show={isOpen}
      onHide={toggle}
      centered
      size="xl"
      fullscreen="md-down"
      backdropClassName="backdrop-high"
      className="modal-pianificazione"
    >
      <Modal.Header closeButton>
        <Modal.Title>Pianificazione rapporto taratura</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-pianificazione-body" data-step={step}>
        <PianificazioneTabs
          step={step}
          setStep={setStep}
          labelSelezione="Bilance"
          selectedCount={bilanceSelected.length}
        />
        <div className="row mt-2 pianificazione-row">
          <div className="col-md-8 pianificazione-selezione">
            <div className="d-flex justify-content-between align-items-end flex-wrap gap-2">
              {!cliente_id ? (
                <div className="form-group mb-2" style={{ width: '300px', maxWidth: '100%' }}>
                  <label className="label-m text-body-secondary">Cliente</label>

                  <SelectAutoCompleteFilter
                    value={cliente}
                    menuPortalTarget={false}
                    onChange={(value: any) =>
                      setCliente(value ? value.value : '')
                    }
                    url={`${API_URL}/bilance/cliente/options`}
                    isMulti={false}
                  />
                </div>
              ) : (
                <div />
              )}

              <div className="mb-2 text-body-secondary">
                {bilanceSelected.length} su{' '}
                {bilanceInfinite?.pages
                  ?.map((pagina) => pagina.results.length)
                  .reduce((a, b) => a + b, 0) ?? 0}{' '}
                bilance selezionate
              </div>
            </div>
            <Card>
              <CardBody>
                <StickyTable
                  isLoading={isLoading || isFetching}
                  className="StickyInModal"
                  onEndReached={hasNextPage ? fetchNextPage : undefined}
                >
                  <thead className="position-sticky top-0 bg-white">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBilanceSelected(
                                bilanceInfinite?.pages
                                  ?.map((pagina) =>
                                    pagina.results.map(
                                      (bilancia) => bilancia.id
                                    )
                                  )
                                  .flat() ?? []
                              )
                            } else {
                              setBilanceSelected([])
                            }
                          }}
                          checked={
                            bilanceSelected.length ===
                            (
                              bilanceInfinite?.pages
                                ?.map((pagina) =>
                                  pagina.results.map((bilancia) => bilancia.id)
                                )
                                .flat() ?? []
                            ).length
                          }
                        />
                      </th>
                      <th>Bilancia</th>
                      <th>Data scadenza</th>
                      <th>Cliente</th>
                    </tr>
                  </thead>
                  <tbody style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {bilanceInfinite?.pages?.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Nessuna bilancia disponibile
                        </td>
                      </tr>
                    )}
                    {bilanceInfinite?.pages?.map((pagina, i) => (
                      <Fragment key={i}>
                        {pagina.results.map((bilancia, j) => (
                          <Fragment key={bilancia.id}>
                            <tr>
                              <td className="p-md">
                                <input
                                  type="checkbox"
                                  checked={bilanceSelected.includes(
                                    bilancia.id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setBilanceSelected([
                                        ...bilanceSelected,
                                        bilancia.id,
                                      ])
                                    } else {
                                      setBilanceSelected(
                                        bilanceSelected.filter(
                                          (id) => id !== bilancia.id
                                        )
                                      )
                                    }
                                  }}
                                />
                              </td>
                              <td>
                                {bilancia.matricola}
                                {bilancia?.sigla
                                  ? `(${bilancia.sigla})`
                                  : ''} -{' '}
                                {getLabelTipoBilancia(bilancia.tipo_bilancia)}
                              </td>
                              <td>
                                {bilancia.data_scadenza_taratura
                                  ? dayjs(
                                      bilancia.data_scadenza_taratura
                                    ).format('DD/MM/YYYY')
                                  : '-'}
                              </td>
                              <td className="p-sm">
                                <div>{bilancia.cliente_ragione_sociale}</div>
                                <div>
                                  {bilancia.sede_data.comune_data?.nome}
                                </div>
                                <div>
                                  {bilancia.sede_data.toponimo_data?.nome}{' '}
                                  {bilancia.sede_data.via}{' '}
                                  {bilancia.sede_data.civico} (
                                  {bilancia.sede_data.provincia_data?.sigla})
                                </div>
                              </td>
                            </tr>
                          </Fragment>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </StickyTable>
                {!isMobile && operatoreField}
              </CardBody>
            </Card>
          </div>
          <div className="col-md-4 pianificazione-dettagli">
            {isMobile && <div className="mb-4">{operatoreField}</div>}
            <div className="form-group">
              <CalendarField
                setDataIntervento={setDataIntervento}
                dataIntervento={dataIntervento}
                type="rapporto_taratura"
              />
            </div>

            <div className="form-group mt-4">
              <label className="label-m text-body-secondary">Note</label>
              <textarea
                className="form-control"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <PianificazioneFooter
        step={step}
        setStep={setStep}
        selectedCount={bilanceSelected.length}
        canSave={bilanceSelected.length > 0 && !!dataIntervento}
        onClose={toggle}
        onSave={() =>
          save({
            bilance: bilanceSelected,
            data_intervento: dataIntervento,
            utente,
            note,
          })
        }
      />
    </Modal>
  )
}
